import { useDirection } from "@radix-ui/react-direction";
import {
  MessageSquarePlus,
  MoreHorizontal,
  Pencil,
  Settings,
  Trash,
} from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuIconItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface IChatFolderActionMenuProps {
  isMenuOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onRenameClick: () => void;
  onDeleteClick: () => void;
  onNewChatClick: () => void;
  onSettingsClick: () => void;
}

export const ChatFolderActionMenu: React.FC<IChatFolderActionMenuProps> = ({
  isMenuOpen,
  onOpenChange,
  onRenameClick,
  onDeleteClick,
  onNewChatClick,
  onSettingsClick,
}) => {
  const { t } = useTranslation("sidebar");
  const dir = useDirection();

  const handleMenuClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRenameClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onRenameClick();
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDeleteClick();
  };

  const handleNewChatClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onNewChatClick();
  };

  const handleSettingsClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onSettingsClick();
  };

  return (
    <DropdownMenu dir={dir} open={isMenuOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild onClick={handleMenuClick}>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "absolute top-1/2 -translate-y-1/2 ltr:right-0 rtl:left-0 h-6 w-7 shrink-0 z-10 opacity-0 transition-opacity duration-150 group-hover/folder-item:opacity-100 text-foreground bg-transparent hover:bg-transparent",
            isMenuOpen && "opacity-100",
          )}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="p-0"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuIconItem
          onClick={handleNewChatClick}
          icon={<MessageSquarePlus className="h-4 w-4" />}
        >
          <span>{t("chat_folder_list.new_chat_in_folder")}</span>
        </DropdownMenuIconItem>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuIconItem
          onClick={handleSettingsClick}
          icon={<Settings className="h-4 w-4" />}
        >
          <span>{t("chat_folder_list.folder_settings.menu_item")}</span>
        </DropdownMenuIconItem>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuIconItem
          onClick={handleRenameClick}
          icon={<Pencil className="h-4 w-4" />}
        >
          <span>{t("chat_folder_list.rename_folder")}</span>
        </DropdownMenuIconItem>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuIconItem
          variant="destructive"
          onClick={handleDeleteClick}
          icon={<Trash className="h-4 w-4" />}
        >
          <span>{t("chat_folder_list.delete_folder")}</span>
        </DropdownMenuIconItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
